import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { t } from 'i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import countries from 'i18n-iso-countries'
import {
  AsYouType,
  Metadata,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
} from 'libphonenumber-js'
import { getValueFromPath } from 'Utils/Helpers/getValueFromPath'
import { DEFAULT_PHONE_PREFIX } from 'Utils/constants'
import './PhoneNumberField.css'

countries.registerLocale(require('i18n-iso-countries/langs/es.json'))

const DEFAULT_MAX_DIGITS = 15

// Territorios con prefijo propio en libphonenumber que i18n-iso-countries no nombra.
const TERRITORY_NAMES = {
  AC: 'Isla Ascensión',
  TA: 'Tristán de Acuña',
}
const metadata = new Metadata()

// React 17 no tiene useId: cada instancia toma un id estable al montarse.
let instanceCounter = 0
const useStableId = () => {
  const idRef = useRef(null)
  if (idRef.current === null) {
    instanceCounter += 1
    idRef.current = `phone-number-${instanceCounter}`
  }
  return idRef.current
}

const normalizeText = text =>
  String(text ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')

const onlyDigits = text => String(text ?? '').replace(/\D/g, '')

// Bandera a partir del código ISO (ej. PA -> 🇵🇦).
export const getCountryFlag = countryCode =>
  String(countryCode || '')
    .toUpperCase()
    .replace(/[A-Z]/g, char =>
      String.fromCodePoint(127397 + char.charCodeAt(0))
    )

// Máxima cantidad de dígitos nacionales del país según libphonenumber.
export const getMaxPhoneDigits = countryCode => {
  try {
    metadata.selectNumberingPlan(countryCode)
    const lengths = metadata.numberingPlan.possibleLengths() || []
    return lengths.length > 0 ? Math.max(...lengths) : DEFAULT_MAX_DIGITS
  } catch (error) {
    return DEFAULT_MAX_DIGITS
  }
}

export const formatPhoneNumber = (number, countryCode) => {
  const digits = onlyDigits(number)
  if (!digits || !countryCode) return digits
  return new AsYouType(countryCode).input(digits)
}

// Valida un valor { number, countryCode } del componente.
export const isValidPhoneNumberValue = value => {
  const number = onlyDigits(typeof value === 'string' ? value : value?.number)
  const countryCode = typeof value === 'string' ? null : value?.countryCode
  if (!number || !countryCode) return false
  try {
    return isValidPhoneNumber(number, countryCode)
  } catch (error) {
    return false
  }
}

const buildCountryList = () =>
  getCountries()
    .map(countryCode => {
      const name =
        countries.getName(countryCode, 'es') ||
        TERRITORY_NAMES[countryCode] ||
        countryCode
      const prefix = `+${getCountryCallingCode(countryCode)}`
      return {
        countryCode,
        name,
        prefix,
        flag: getCountryFlag(countryCode),
        searchText: normalizeText(`${name} ${countryCode} ${prefix}`),
      }
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'es'))

export const PhoneNumberField = ({
  formik,
  fieldName,
  fieldToCheck,
  value: externalValue,
  onChange,
  onBlur,
  optional = false,
  id,
  defaultPrefix = DEFAULT_PHONE_PREFIX,
  preferredCountries = ['PA'],
  placeholder,
  disabled = false,
  error,
  maxDigits,
  testId = 'number',
  'data-testid': dataTestId,
}) => {
  const generatedId = useStableId()
  const inputId = id || generatedId
  const listId = `${inputId}-countries`
  const errorId = `${inputId}-error`

  const containerRef = useRef(null)
  const inputRef = useRef(null)
  const countryButtonRef = useRef(null)
  const searchRef = useRef(null)
  const listRef = useRef(null)

  const isUsingFormik = Boolean(formik && fieldName)
  const [localValue, setLocalValue] = useState({
    Prefix: defaultPrefix,
    number: '',
  })
  const [isOpen, setIsOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)

  const rawValue = isUsingFormik
    ? getValueFromPath(formik.values, fieldName) || {}
    : externalValue ?? localValue
  // Compatibilidad: algunos consumidores envían solo el número como string.
  const value =
    typeof rawValue === 'string'
      ? { ...localValue, number: rawValue }
      : rawValue || {}
  const number = onlyDigits(value.number)

  const countryList = useMemo(buildCountryList, [])
  const preferredSet = useMemo(
    () => new Set(preferredCountries.map(code => code.toUpperCase())),
    [preferredCountries]
  )

  // El país se identifica por countryCode; el prefijo solo no basta (+1 = EE. UU., Canadá...).
  const findCountry = (countryCode, prefix) =>
    countryList.find(country => country.countryCode === countryCode) ||
    countryList.find(
      country =>
        country.prefix === prefix && preferredSet.has(country.countryCode)
    ) ||
    countryList.find(country => country.prefix === prefix) ||
    null

  const selectedCountry =
    findCountry(value.countryCode, value.Prefix || defaultPrefix) ||
    findCountry(null, defaultPrefix)
  const countryCode = selectedCountry?.countryCode
  const digitLimit = maxDigits || getMaxPhoneDigits(countryCode)

  // Completa countryCode en formik para valores guardados que solo tienen Prefix.
  useEffect(() => {
    if (isUsingFormik && countryCode && !value.countryCode) {
      formik.setFieldValue(`${fieldName}.countryCode`, countryCode, false)
    }
  }, [isUsingFormik, countryCode, value.countryCode])

  const emitChange = changes => {
    if (isUsingFormik) {
      Object.entries(changes).forEach(([key, fieldValue]) =>
        formik.setFieldValue(`${fieldName}.${key}`, fieldValue)
      )
      return
    }

    const updated = {
      ...value,
      Prefix: selectedCountry?.prefix || defaultPrefix,
      countryCode,
      number,
      ...changes,
    }
    setLocalValue(updated)
    onChange?.(updated)
  }

  const formattedNumber = formatPhoneNumber(number, countryCode)

  const handleInputChange = event => {
    const typed = event.target.value
    let digits = onlyDigits(typed)
    // Al borrar un separador del formato (espacio, guion) se elimina el dígito anterior.
    if (digits === number && typed.length < formattedNumber.length) {
      digits = digits.slice(0, -1)
    }
    emitChange({ number: digits.slice(0, digitLimit) })
  }

  const closeList = ({ focusButton = false } = {}) => {
    setIsOpen(false)
    setSearchText('')
    if (focusButton) countryButtonRef.current?.focus()
  }

  const handleSelectCountry = country => {
    emitChange({
      Prefix: country.prefix,
      countryCode: country.countryCode,
      number: number.slice(
        0,
        maxDigits || getMaxPhoneDigits(country.countryCode)
      ),
    })
    closeList()
    inputRef.current?.focus()
  }

  // Cerrar al hacer clic fuera
  useEffect(() => {
    if (!isOpen) return undefined

    const handleClickOutside = event => {
      if (!containerRef.current?.contains(event.target)) closeList()
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const normalizedSearch = normalizeText(searchText.trim()).replace(/^\+/, '')
  const visibleCountries = useMemo(() => {
    if (normalizedSearch) {
      return countryList.filter(country =>
        country.searchText.includes(normalizedSearch)
      )
    }
    const preferred = preferredCountries
      .map(code => countryList.find(c => c.countryCode === code.toUpperCase()))
      .filter(Boolean)
    return [
      ...preferred,
      ...countryList.filter(country => !preferredSet.has(country.countryCode)),
    ]
  }, [normalizedSearch, countryList, preferredCountries, preferredSet])
  const preferredCount = normalizedSearch
    ? 0
    : visibleCountries.filter(c => preferredSet.has(c.countryCode)).length

  // Al abrir: enfocar el buscador y marcar el país seleccionado
  useEffect(() => {
    if (!isOpen) return
    searchRef.current?.focus()
    const selectedIndex = visibleCountries.findIndex(
      country => country.countryCode === countryCode
    )
    setActiveIndex(Math.max(selectedIndex, 0))
  }, [isOpen])

  useEffect(() => {
    if (normalizedSearch) setActiveIndex(0)
  }, [normalizedSearch])

  useEffect(() => {
    if (!isOpen) return
    const activeOption = listRef.current?.querySelector(
      `[data-index="${activeIndex}"]`
    )
    activeOption?.scrollIntoView?.({ block: 'nearest' })
  }, [activeIndex, isOpen])

  const handleSearchKeyDown = event => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex(index =>
        Math.min(index + 1, Math.max(visibleCountries.length - 1, 0))
      )
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex(index => Math.max(index - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const country = visibleCountries[activeIndex]
      if (country) handleSelectCountry(country)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      closeList({ focusButton: true })
    } else if (event.key === 'Tab') {
      closeList()
    }
  }

  const handleBlur = event => {
    if (isUsingFormik) formik.handleBlur?.(event)
    onBlur?.(event)
  }

  // Error: el prop error tiene prioridad; si no, se toma de formik.
  const formikTouched =
    isUsingFormik &&
    (getValueFromPath(formik.touched, `${fieldName}.number`) ||
      (fieldToCheck && Object.hasOwn(formik.touched || {}, fieldToCheck)))
  const formikError =
    isUsingFormik &&
    (getValueFromPath(formik.errors, `${fieldName}.number`) ||
      (fieldToCheck && formik.errors?.[fieldToCheck]?.[0]?.number))
  const hasError =
    error !== undefined ? Boolean(error) : Boolean(formikTouched && formikError)
  const errorMessage = typeof error === 'string' ? error : null

  const inputPlaceholder =
    placeholder ??
    (countryCode === 'PA' ? '0000-0000' : t('PHONE_NUMBER_FIELD.PLACEHOLDER'))
  const inputTestId = dataTestId || `input-phone-${testId}`

  return (
    <div
      ref={containerRef}
      className={`phone-number-field${hasError ? ' is-invalid' : ''}${
        disabled ? ' is-disabled' : ''
      }${isOpen ? ' is-open' : ''}`}>
      <div className="phone-number-field-control">
        <button
          ref={countryButtonRef}
          type="button"
          className="phone-number-field-country"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listId : undefined}
          aria-label={t('PHONE_NUMBER_FIELD.COUNTRY_BUTTON', {
            country: selectedCountry?.name || '',
            prefix: selectedCountry?.prefix || '',
          })}
          onClick={() => (isOpen ? closeList() : setIsOpen(true))}
          data-testid={`btn-select-country-${testId}`}>
          <span className="phone-number-field-flag" aria-hidden="true">
            {selectedCountry?.flag}
          </span>
          <span className="phone-number-field-prefix">
            {selectedCountry?.prefix || defaultPrefix}
          </span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className="phone-number-field-chevron"
            aria-hidden="true"
          />
        </button>

        <input
          ref={inputRef}
          id={inputId}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          className="phone-number-field-input"
          name={isUsingFormik ? `${fieldName}.number` : undefined}
          placeholder={inputPlaceholder}
          value={formattedNumber}
          disabled={disabled}
          aria-invalid={hasError || undefined}
          aria-required={!optional || undefined}
          aria-describedby={errorMessage ? errorId : undefined}
          onChange={handleInputChange}
          onBlur={handleBlur}
          data-testid={inputTestId}
        />
      </div>

      {isOpen && (
        <div className="phone-number-field-dropdown">
          <input
            ref={searchRef}
            type="text"
            className="phone-number-field-search"
            placeholder={t('PHONE_NUMBER_FIELD.SEARCH_COUNTRY')}
            value={searchText}
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={
              visibleCountries[activeIndex]
                ? `${listId}-${visibleCountries[activeIndex].countryCode}`
                : undefined
            }
            onChange={event => setSearchText(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            data-testid={`input-search-country-${testId}`}
          />

          {visibleCountries.length === 0 ? (
            <p className="phone-number-field-empty">
              {t('PHONE_NUMBER_FIELD.NO_RESULTS')}
            </p>
          ) : (
            <ul
              ref={listRef}
              id={listId}
              role="listbox"
              className="phone-number-field-list"
              aria-label={t('PHONE_NUMBER_FIELD.COUNTRIES')}>
              {visibleCountries.map((country, index) => {
                const isSelected = country.countryCode === countryCode
                return (
                  <React.Fragment key={country.countryCode}>
                    {index === 0 && preferredCount > 0 && (
                      <li
                        className="phone-number-field-group"
                        role="presentation">
                        {t('PHONE_NUMBER_FIELD.FREQUENT')}
                      </li>
                    )}
                    {index === preferredCount && preferredCount > 0 && (
                      <li
                        className="phone-number-field-group"
                        role="presentation">
                        {t('PHONE_NUMBER_FIELD.ALL_COUNTRIES')}
                      </li>
                    )}
                    <li
                      id={`${listId}-${country.countryCode}`}
                      role="option"
                      aria-selected={isSelected}
                      data-index={index}
                      className={`phone-number-field-option${
                        isSelected ? ' is-selected' : ''
                      }${index === activeIndex ? ' is-active' : ''}`}
                      onMouseEnter={() => setActiveIndex(index)}
                      onMouseDown={event => event.preventDefault()}
                      onClick={() => handleSelectCountry(country)}
                      data-testid={`item-country-${testId}-${country.countryCode}`}>
                      <span
                        className="phone-number-field-flag"
                        aria-hidden="true">
                        {country.flag}
                      </span>
                      <span className="phone-number-field-option-name">
                        {country.name}
                      </span>
                      <span className="phone-number-field-option-prefix">
                        {country.prefix}
                      </span>
                    </li>
                  </React.Fragment>
                )
              })}
            </ul>
          )}
        </div>
      )}

      {errorMessage && (
        <p className="phone-number-field-error" id={errorId} role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  )
}

PhoneNumberField.propTypes = {
  formik: PropTypes.shape({
    setFieldValue: PropTypes.func,
    handleBlur: PropTypes.func,
    values: PropTypes.object,
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  fieldName: PropTypes.string,
  fieldToCheck: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      Prefix: PropTypes.string,
      number: PropTypes.string,
      countryCode: PropTypes.string,
    }),
  ]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  optional: PropTypes.bool,
  id: PropTypes.string,
  defaultPrefix: PropTypes.string,
  preferredCountries: PropTypes.arrayOf(PropTypes.string),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  maxDigits: PropTypes.number,
  testId: PropTypes.string,
  'data-testid': PropTypes.string,
}

export default PhoneNumberField
